import { useEffect, useMemo, useState } from 'react';
import classNames from 'classnames';
import { useFlagMutations, useFlags } from '../../hooks';
import type {
  FlagAccess,
  IFlag,
  IFlagEnvironment,
  IFlagEnvironmentInput,
  IFlagInput,
  IWorkOSEnvironment,
  IWorkOSOrganisation,
} from '../../types';
import styles from './Flags.module.scss';

const ACCESS: { id: FlagAccess; label: string; hint: string }[] = [
  { id: 'ALL', label: 'Everyone', hint: 'every organisation and user in this environment' },
  { id: 'SOME', label: 'Selected', hint: 'only the organisations and users ticked below' },
  { id: 'NONE', label: 'Nobody', hint: 'flag stays off for all even though it is enabled' },
];

const slugify = (value: string): string =>
  value
    .toLowerCase()
    .trim()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '');

const describe = (
  state: IFlagEnvironment,
  orgs: IWorkOSOrganisation[],
): { tone: 'on' | 'partial' | 'off'; label: string } => {
  if (!state.enabled) {
    return { tone: 'off', label: 'Off' };
  }
  if (state.access === 'ALL') {
    return { tone: 'on', label: 'On for all' };
  }
  if (state.access === 'NONE') {
    return { tone: 'off', label: 'Enabled, nobody' };
  }
  const count =
    state.organisation_ids.filter((id) => orgs.some((org) => org.id === id)).length +
    state.user_ids.length;
  return {
    tone: 'partial',
    label: `${count} target${count === 1 ? '' : 's'}${state.default_enabled ? ' + default' : ''}`,
  };
};

interface EnvironmentEditorProps {
  environment: IWorkOSEnvironment;
  state: IFlagEnvironment;
  organisations: IWorkOSOrganisation[];
  saving: boolean;
  onSave: (input: IFlagEnvironmentInput) => void;
}

const EnvironmentEditor = ({
  environment,
  state,
  organisations,
  saving,
  onSave,
}: EnvironmentEditorProps) => {
  const [draft, setDraft] = useState<IFlagEnvironmentInput>({
    enabled: state.enabled,
    default_enabled: state.default_enabled,
    access: state.access,
    organisation_ids: state.organisation_ids,
    user_ids: state.user_ids,
  });
  useEffect(
    () =>
      setDraft({
        enabled: state.enabled,
        default_enabled: state.default_enabled,
        access: state.access,
        organisation_ids: state.organisation_ids,
        user_ids: state.user_ids,
      }),
    [state],
  );
  const [userDraft, setUserDraft] = useState('');
  const dirty =
    JSON.stringify(draft) !==
    JSON.stringify({
      enabled: state.enabled,
      default_enabled: state.default_enabled,
      access: state.access,
      organisation_ids: state.organisation_ids,
      user_ids: state.user_ids,
    });
  const toggleOrg = (id: string) =>
    setDraft((current) => ({
      ...current,
      organisation_ids: current.organisation_ids.includes(id)
        ? current.organisation_ids.filter((item) => item !== id)
        : [...current.organisation_ids, id],
    }));
  const summary = describe({ ...state, ...draft }, organisations);

  return (
    <section className={styles.envCard} data-prod={environment.stage === 'prod'}>
      <header className={styles.envHeader}>
        <div>
          <h3 className={styles.envName}>
            {environment.name}
            {environment.stage ? (
              <span className={styles.envStage}>{environment.stage}</span>
            ) : null}
          </h3>
          <span className={classNames(styles.state, styles[summary.tone])}>{summary.label}</span>
        </div>
        <label className={styles.switch}>
          <input
            type="checkbox"
            checked={draft.enabled}
            onChange={(event) =>
              setDraft((current) => ({ ...current, enabled: event.target.checked }))
            }
            aria-label={`${environment.name} enabled`}
          />
          <span className={styles.track} />
          <span className={styles.switchLabel}>{draft.enabled ? 'Enabled' : 'Disabled'}</span>
        </label>
      </header>
      {draft.enabled ? (
        <>
          <div
            className={styles.accessRow}
            role="radiogroup"
            aria-label={`${environment.name} access`}
          >
            {ACCESS.map((option) => (
              <button
                key={option.id}
                type="button"
                role="radio"
                aria-checked={draft.access === option.id}
                className={draft.access === option.id ? styles.accessOn : styles.access}
                onClick={() => setDraft((current) => ({ ...current, access: option.id }))}
                title={option.hint}
              >
                {option.label}
              </button>
            ))}
            <label className={styles.inlineCheck}>
              <input
                type="checkbox"
                checked={draft.default_enabled}
                onChange={(event) =>
                  setDraft((current) => ({ ...current, default_enabled: event.target.checked }))
                }
              />
              Default on
            </label>
          </div>
          {draft.access === 'SOME' ? (
            <div className={styles.targets}>
              <div className={styles.targetGroup}>
                <span className={styles.targetLabel}>Organisations</span>
                <div className={styles.orgGrid}>
                  {organisations.length === 0 ? (
                    <span className={styles.muted}>No organisations in this environment.</span>
                  ) : null}
                  {organisations.map((org) => (
                    <label
                      key={org.id}
                      className={styles.orgOption}
                      data-on={draft.organisation_ids.includes(org.id)}
                    >
                      <input
                        type="checkbox"
                        checked={draft.organisation_ids.includes(org.id)}
                        onChange={() => toggleOrg(org.id)}
                      />
                      <span>{org.name}</span>
                      <span className={styles.orgUsers}>{org.users}</span>
                    </label>
                  ))}
                </div>
              </div>
              <div className={styles.targetGroup}>
                <span className={styles.targetLabel}>Users (WorkOS user ids)</span>
                <div className={styles.userList}>
                  {draft.user_ids.map((id) => (
                    <span key={id} className={styles.userChip}>
                      {id}
                      <button
                        type="button"
                        onClick={() =>
                          setDraft((current) => ({
                            ...current,
                            user_ids: current.user_ids.filter((item) => item !== id),
                          }))
                        }
                        aria-label={`Remove ${id}`}
                      >
                        ×
                      </button>
                    </span>
                  ))}
                  <form
                    className={styles.userAdd}
                    onSubmit={(event) => {
                      event.preventDefault();
                      const value = userDraft.trim();
                      if (value && !draft.user_ids.includes(value)) {
                        setDraft((current) => ({
                          ...current,
                          user_ids: [...current.user_ids, value],
                        }));
                      }
                      setUserDraft('');
                    }}
                  >
                    <input
                      value={userDraft}
                      onChange={(event) => setUserDraft(event.target.value)}
                      placeholder="user_01…"
                      aria-label="Add user id"
                    />
                    <button type="submit" className={styles.ghostSmall}>
                      Add
                    </button>
                  </form>
                </div>
              </div>
            </div>
          ) : null}
        </>
      ) : null}
      {dirty ? (
        <div className={styles.envActions}>
          <button
            type="button"
            className={styles.ghostSmall}
            onClick={() =>
              setDraft({
                enabled: state.enabled,
                default_enabled: state.default_enabled,
                access: state.access,
                organisation_ids: state.organisation_ids,
                user_ids: state.user_ids,
              })
            }
          >
            Discard
          </button>
          <button
            type="button"
            className={styles.primarySmall}
            disabled={saving}
            onClick={() => onSave(draft)}
          >
            {saving ? 'Saving…' : environment.stage === 'prod' ? 'Save to Hub (prod)' : 'Save'}
          </button>
        </div>
      ) : null}
    </section>
  );
};

const FlagForm = ({
  initial,
  title,
  saving,
  onClose,
  onSave,
  onDelete,
}: {
  initial: IFlagInput;
  title: string;
  saving: boolean;
  onClose: () => void;
  onSave: (input: IFlagInput) => void;
  onDelete?: () => void;
}) => {
  const [draft, setDraft] = useState<IFlagInput>(initial);
  const [slugTouched, setSlugTouched] = useState(Boolean(initial.slug));
  useEffect(() => setDraft(initial), [initial]);
  return (
    <form
      className={styles.form}
      onSubmit={(event) => {
        event.preventDefault();
        onSave({
          ...draft,
          name: draft.name.trim(),
          slug: draft.slug.trim(),
          description: draft.description?.trim() || null,
        });
      }}
    >
      <h3 className={styles.formTitle}>{title}</h3>
      <label className={styles.field}>
        <span>Name</span>
        <input
          value={draft.name}
          onChange={(event) =>
            setDraft((current) => ({
              ...current,
              name: event.target.value,
              slug: slugTouched ? current.slug : slugify(event.target.value),
            }))
          }
          required
        />
      </label>
      <label className={styles.field}>
        <span>Slug (what the code checks)</span>
        <input
          value={draft.slug}
          onChange={(event) => {
            setSlugTouched(true);
            setDraft((current) => ({ ...current, slug: event.target.value }));
          }}
          required
          pattern="[a-z0-9_\-]+"
          disabled={Boolean(onDelete)}
        />
        <small>
          {onDelete
            ? 'Slugs cannot change once code reads them.'
            : 'Lowercase, digits, hyphens and underscores.'}
        </small>
      </label>
      <label className={styles.field}>
        <span>Description</span>
        <textarea
          rows={3}
          value={draft.description ?? ''}
          onChange={(event) =>
            setDraft((current) => ({ ...current, description: event.target.value }))
          }
          placeholder="What it switches on, which ticket, and when it can be removed."
        />
      </label>
      <label className={styles.field}>
        <span>Tags</span>
        <input
          value={draft.tags.join(', ')}
          onChange={(event) =>
            setDraft((current) => ({
              ...current,
              tags: event.target.value
                .split(',')
                .map((tag) => tag.trim())
                .filter(Boolean),
            }))
          }
          placeholder="entitlement, rollout"
        />
      </label>
      <div className={styles.formActions}>
        {onDelete ? (
          <button type="button" className={styles.danger} onClick={onDelete} disabled={saving}>
            Delete flag
          </button>
        ) : null}
        <span className={styles.spacer} />
        <button type="button" className={styles.ghostSmall} onClick={onClose}>
          Cancel
        </button>
        <button
          type="submit"
          className={styles.primarySmall}
          disabled={saving || !draft.name.trim() || !draft.slug.trim()}
        >
          {saving ? 'Saving…' : 'Save'}
        </button>
      </div>
    </form>
  );
};

export const Flags = () => {
  const { data, isLoading, error } = useFlags();
  const { create, update, remove, updateEnvironment } = useFlagMutations();
  const [selected, setSelected] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const [editingMeta, setEditingMeta] = useState(false);
  const [search, setSearch] = useState('');
  const saving =
    create.isLoading || update.isLoading || remove.isLoading || updateEnvironment.isLoading;
  const failure = [create.error, update.error, remove.error, updateEnvironment.error].find(
    Boolean,
  ) as { message?: string } | undefined;
  const warnings = (updateEnvironment.data as { warnings?: string[] } | undefined)?.warnings ?? [];

  const environments = data?.environments ?? [];
  const orgsByEnv = useMemo(() => {
    const map = new Map<string, IWorkOSOrganisation[]>();
    (data?.organisations ?? []).forEach((org) =>
      map.set(org.environment_id, [...(map.get(org.environment_id) ?? []), org]),
    );
    return map;
  }, [data]);
  const flags = useMemo(() => {
    const term = search.trim().toLowerCase();
    return (data?.flags ?? []).filter(
      (flag) =>
        !term ||
        flag.name.toLowerCase().includes(term) ||
        flag.slug.includes(term) ||
        flag.tags.some((tag) => tag.includes(term)),
    );
  }, [data, search]);
  const current: IFlag | null = data?.flags.find((flag) => flag.id === selected) ?? null;

  const stale = (flag: IFlag) =>
    flag.environments
      .filter((state) => state.environment_id !== 'environment_01J6AEAT5XSSTH7MBBJZBWJM5R')
      .every((state) => state.enabled && state.access === 'ALL');

  if (isLoading) return <p className={styles.muted}>Loading flags from WorkOS…</p>;
  if (error || !data)
    return (
      <p className={styles.error}>
        Could not load flags: {(error as { message?: string })?.message ?? 'unknown error'}
      </p>
    );

  return (
    <div className={styles.page}>
      <header className={styles.header}>
        <div>
          <h1 className={styles.title}>Feature flags</h1>
          <p className={styles.subtitle}>
            {data.flags.length} flags in the openr WorkOS project. Writes go through the{' '}
            {data.write_adapter === 'dashboard'
              ? 'WorkOS dashboard adapter'
              : data.write_adapter === 'mock'
              ? 'local mock'
              : data.write_adapter}{' '}
            adapter.
          </p>
        </div>
        <div className={styles.headerActions}>
          <input
            className={styles.search}
            type="search"
            placeholder="Search flags"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            aria-label="Search flags"
          />
          <button
            type="button"
            className={styles.primary}
            onClick={() => {
              setCreating(true);
              setSelected(null);
            }}
          >
            + New flag
          </button>
        </div>
      </header>
      {failure ? (
        <p className={styles.error}>{failure.message ?? 'That change was not saved.'}</p>
      ) : null}
      {warnings.map((warning) => (
        <p key={warning} className={styles.warning}>
          {warning}
        </p>
      ))}
      <div className={current || creating ? styles.split : undefined}>
        <div className={styles.matrixCard}>
          <table className={styles.matrix}>
            <thead>
              <tr>
                <th className={styles.thFlag}>Flag</th>
                {environments.map((environment) => (
                  <th
                    key={environment.id}
                    className={styles.thEnv}
                    data-prod={environment.stage === 'prod'}
                  >
                    {environment.name}
                    {environment.stage ? (
                      <span className={styles.envStage}>{environment.stage}</span>
                    ) : null}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {flags.map((flag, index) => (
                <tr
                  key={flag.id}
                  className={classNames(styles.row, { [styles.rowOn]: flag.id === selected })}
                  style={{ animationDelay: `${Math.min(index, 12) * 30}ms` }}
                  onClick={() => {
                    setSelected(flag.id);
                    setCreating(false);
                    setEditingMeta(false);
                  }}
                  tabIndex={0}
                  onKeyDown={(event) => (event.key === 'Enter' ? setSelected(flag.id) : undefined)}
                >
                  <td className={styles.tdFlag}>
                    <div className={styles.flagName}>
                      {flag.name}
                      {flag.tags.map((tag) => (
                        <span key={tag} className={styles.tag}>
                          {tag}
                        </span>
                      ))}
                      {stale(flag) ? (
                        <span
                          className={styles.staleTag}
                          title="On for everyone in every app environment: candidate for removal"
                        >
                          retire?
                        </span>
                      ) : null}
                    </div>
                    <code className={styles.slug}>{flag.slug}</code>
                  </td>
                  {environments.map((environment) => {
                    const state = flag.environments.find(
                      (item) => item.environment_id === environment.id,
                    );
                    const summary = state
                      ? describe(state, orgsByEnv.get(environment.id) ?? [])
                      : { tone: 'off' as const, label: '—' };
                    return (
                      <td key={environment.id} className={styles.tdEnv}>
                        <span className={classNames(styles.state, styles[summary.tone])}>
                          {summary.label}
                        </span>
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {creating ? (
          <aside className={styles.panel}>
            <FlagForm
              initial={{ name: '', slug: '', description: null, tags: [] }}
              title="New flag"
              saving={saving}
              onClose={() => setCreating(false)}
              onSave={(input) =>
                create.mutate(input, {
                  onSuccess: (result) => {
                    setCreating(false);
                    setSelected(result.data.id);
                  },
                })
              }
            />
            <p className={styles.muted}>
              Created off in every environment. Enable it per environment once it exists.
            </p>
          </aside>
        ) : null}
        {current ? (
          <aside className={styles.panel} aria-label={current.name}>
            <header className={styles.panelHeader}>
              <div>
                <h2 className={styles.panelTitle}>{current.name}</h2>
                <code className={styles.slug}>{current.slug}</code>
                {current.description ? (
                  <p className={styles.description}>{current.description}</p>
                ) : null}
                <p className={styles.meta}>
                  {current.owner_email ? `Owner ${current.owner_email} · ` : ''}Updated{' '}
                  {new Date(current.updated_at).toLocaleDateString('en-GB')}
                </p>
              </div>
              <div className={styles.panelTools}>
                <button
                  type="button"
                  className={styles.ghostSmall}
                  onClick={() => setEditingMeta((value) => !value)}
                >
                  {editingMeta ? 'Close' : 'Edit details'}
                </button>
                <button
                  type="button"
                  className={styles.close}
                  onClick={() => setSelected(null)}
                  aria-label="Close"
                >
                  ×
                </button>
              </div>
            </header>
            {editingMeta ? (
              <FlagForm
                initial={{
                  name: current.name,
                  slug: current.slug,
                  description: current.description,
                  tags: current.tags,
                }}
                title="Details"
                saving={saving}
                onClose={() => setEditingMeta(false)}
                onSave={(input) =>
                  update.mutate(
                    { id: current.id, input },
                    { onSuccess: () => setEditingMeta(false) },
                  )
                }
                onDelete={() => {
                  if (
                    globalThis.confirm(
                      `Delete flag ${current.slug} in every environment? Code that reads it will see it as off.`,
                    )
                  ) {
                    remove.mutate(current.id, { onSuccess: () => setSelected(null) });
                  }
                }}
              />
            ) : null}
            <div className={styles.envList}>
              {environments.map((environment) => {
                const state = current.environments.find(
                  (item) => item.environment_id === environment.id,
                );
                return state ? (
                  <EnvironmentEditor
                    key={environment.id}
                    environment={environment}
                    state={state}
                    organisations={orgsByEnv.get(environment.id) ?? []}
                    saving={saving}
                    onSave={(input) =>
                      updateEnvironment.mutate({
                        flagId: current.id,
                        flagEnvironmentId: state.id,
                        input,
                      })
                    }
                  />
                ) : null;
              })}
            </div>
          </aside>
        ) : null}
      </div>
    </div>
  );
};
